/*
 * Two-finger swipe left/right = change page, on a touch screen and on a touchpad.
 * Kept free of DOM/Fabric so the thresholds can be unit-tested (tests/gestures.test.ts).
 *
 *   touch screen   two fingers move sideways together, quickly, without pinching — only while the page is
 *                  shown whole ("พอดีจอ"); when zoomed in, two fingers move the view as before
 *   touchpad       two fingers sideways = horizontal scroll (wheel deltaX); up/down still zooms
 * Direction: content moves left = next page, right = previous page.
 */

/* ---------- touch screen ---------- */

/** the two-finger stroke must be quicker than this (ms) … */
export const SWIPE_MAX_MS = 600;
/** … move its midpoint at least this far sideways (CSS px) … */
export const SWIPE_MIN_DX = 80;
/** … clearly more sideways than up/down … */
export const SWIPE_MIN_RATIO = 2;
/** … and keep the fingers about the same distance apart (more than this = a pinch) */
export const SWIPE_MAX_PINCH = 0.15;

/** dx/dy: movement of the point between the fingers; scale: finger distance at the end ÷ at the start */
export interface TwoFingerStroke { dx: number; dy: number; dt: number; scale: number }

/** -1 = previous page, +1 = next page, 0 = not a page swipe */
export function twoFingerSwipe(s: TwoFingerStroke): -1 | 0 | 1 {
  if (s.dt > SWIPE_MAX_MS || Math.abs(s.scale - 1) > SWIPE_MAX_PINCH) return 0;
  if (Math.abs(s.dx) < SWIPE_MIN_DX || Math.abs(s.dx) < SWIPE_MIN_RATIO * Math.abs(s.dy)) return 0;
  return s.dx < 0 ? 1 : -1;
}

/*
 * Two fingers tapped together = the menu of the object under them (iPad has no right click; the person chose this
 * over a long press). Quick, no pinch, no slide — so it never takes a pinch/pan or a page swipe.
 */
/** both fingers lifted within this (ms) of the second one landing … */
export const TAP2_MAX_MS = 350;
/** … the point between them moved at most this far (CSS px) … */
export const TAP2_MAX_MOVE = 12;
/** … and their distance changed at most this much (a pinch is more) */
export const TAP2_MAX_PINCH = 0.06;

/** whether a two-finger touch was a tap (not a pinch, pan or swipe) */
export function twoFingerTap(s: TwoFingerStroke): boolean {
  return s.dt <= TAP2_MAX_MS && Math.hypot(s.dx, s.dy) <= TAP2_MAX_MOVE && Math.abs(s.scale - 1) <= TAP2_MAX_PINCH;
}

/*
 * Where the page sits after zooming (choice ก, 19 Oct 2026): zooming follows the fingers / pointer, but
 *   the page smaller than the work area on a side → centred on that side (it never drifts off to a corner)
 *   the page bigger than the work area             → it always covers the middle of the work area (never lost)
 */
/** one side: page from `start` to `end` (page units), shown at zoom `z` with offset `t`, in a view from `v0` to `v1` (CSS px) */
export function keepSide(t: number, z: number, start: number, end: number, v0: number, v1: number, margin = 16): number {
  const size = (end - start) * z, room = v1 - v0;
  if (size <= room - 2 * margin) return v0 + (room - size) / 2 - start * z;   // centred
  const mid = v0 + room / 2;
  // the page edges stay on both sides of the middle line
  const lo = mid - end * z, hi = mid - start * z;
  return Math.min(hi, Math.max(lo, t));
}

/** the page counts as "shown whole" when the zoom is within 3 % of the fit zoom */
export const atFit = (zoom: number, fitZoom: number) => fitZoom > 0 && Math.abs(zoom - fitZoom) / fitZoom < 0.03;

/* ---------- touchpad ---------- */

/** sideways scroll needed to turn a page (px of wheel delta) */
export const WHEEL_MIN_DX = 120;
/** a pause this long ends one swipe (the touchpad keeps sending scroll after the fingers lift) */
export const WHEEL_GAP_MS = 250;

/**
 * Turns horizontal wheel events into at most one page turn per swipe.
 * feed() returns the direction once the swipe has gone far enough, then 0 until the swipe ends.
 */
export class WheelSwipe {
  private sum = 0;
  private last = -Infinity;
  private fired = false;

  /** is this wheel event a sideways swipe (not a zoom)? ctrl = touchpad pinch in Chrome/Safari */
  static sideways(dx: number, dy: number, ctrl: boolean) { return !ctrl && Math.abs(dx) > Math.abs(dy) && dx !== 0; }

  feed(dx: number, time: number): -1 | 0 | 1 {
    if (time - this.last > WHEEL_GAP_MS) { this.sum = 0; this.fired = false; }
    this.last = time;
    if (this.fired) return 0;
    this.sum += dx;
    if (Math.abs(this.sum) < WHEEL_MIN_DX) return 0;
    this.fired = true;
    return this.sum > 0 ? 1 : -1;     // scrolling right (content moves left) = next page
  }
}
